import { Loan, LoanAllocation, LoanTableEntry } from '../definitions/loan';

/**
 * Building-loan allocation + projection helpers (pure, no Angular deps).
 *
 * A "building loan" is a SINGLE `Loan` attached to a building
 * (`Loan.buildingGroupUID`) holding the FULL principal. Each unit's financial
 * impact is a *derived view*: at read time we project the building loan onto a
 * unit by scaling its amortization table by the unit's `share`. The shares sum
 * to exactly 1, so the per-unit projections recompose the whole loan exactly.
 *
 * This module is imported by both `LoanStore` (Angular) and the pure
 * `cashflow.aggregation` reducer, so it must stay dependency-free.
 */

/** A unit + its purchase price, used to derive a pro-rata allocation. */
export interface UnitPurchasePrice {
    propertyUID: string;
    price: number | null | undefined;
}

export type AllocationResult =
    | { ok: true; allocations: LoanAllocation[] }
    | { ok: false; missing: string[] };

/**
 * Compute a pro-rata allocation of a building loan across its units, weighted
 * by each unit's purchase price.
 *
 * Returns `{ ok: false, missing }` when there are no units or any unit lacks a
 * positive purchase price (`missing` lists those `propertyUID`s) — callers MUST
 * block creation in that case rather than allocate approximately. On success,
 * `share` values sum to EXACTLY 1: the last unit absorbs the rounding remainder
 * so the projected parts always recompose the whole.
 */
export function computeAllocationsByPurchasePrice(units: UnitPurchasePrice[]): AllocationResult {
    if (!units || units.length === 0) {
        return { ok: false, missing: [] };
    }

    const missing = units
        .filter(u => !(typeof u.price === 'number' && isFinite(u.price) && u.price > 0))
        .map(u => u.propertyUID);
    if (missing.length > 0) {
        return { ok: false, missing };
    }

    const total = units.reduce((sum, u) => sum + (u.price as number), 0);
    if (total <= 0) {
        return { ok: false, missing: units.map(u => u.propertyUID) };
    }

    const allocations: LoanAllocation[] = [];
    let accumulated = 0;
    units.forEach((u, index) => {
        const price = u.price as number;
        // Last unit takes (1 − Σothers) so the shares sum to exactly 1.
        const share = index === units.length - 1 ? 1 - accumulated : price / total;
        if (index !== units.length - 1) {
            accumulated += share;
        }
        allocations.push({ propertyUID: u.propertyUID, share, purchasePrice: price });
    });

    return { ok: true, allocations };
}

/**
 * Project a single building loan onto one allocated unit by scaling its
 * principal and amortization table by the unit's `share`. The returned object
 * is a real `Loan` instance (so model methods like `getCashFlowForPeriod` work)
 * carrying `propertyUID = unit` and the SAME `loanUID` as the parent (so "view
 * loan" links resolve to the real building loan). It is NOT a building loan
 * itself (`buildingGroupUID`/`allocations` are dropped on the clone).
 */
export function scaleLoanToShare(loan: Loan, allocation: LoanAllocation): Loan {
    const share = allocation.share;

    const projected = new Loan({
        ...loan,
        propertyUID: allocation.propertyUID,
        buildingGroupUID: undefined,
        allocations: undefined,
        // The parent's amortization table already incorporates any lifecycle
        // amendments; we scale that table below. Dropping the events here avoids
        // the clone's constructor re-running the engine on the reduced principal
        // (which would mis-apply euro-denominated events) only to be overwritten.
        loanEvents: [],
        capital: {
            ...loan.capital,
            nominalAmount: loan.capital.nominalAmount * share
        }
    });

    // The constructor recomputed the table from the scaled principal; overwrite
    // it with an EXACT scaling of the parent table so that, across all units,
    // the projected parts sum back to the parent loan to the cent.
    projected.loanTable = (loan.loanTable ?? []).map(entry => {
        const scaled = new LoanTableEntry();
        scaled.period = entry.period;
        scaled.date = new Date(entry.date);
        scaled.startingPrincipal = entry.startingPrincipal * share;
        scaled.interest = entry.interest * share;
        scaled.principalRepayment = entry.principalRepayment * share;
        scaled.endingPrincipal = entry.endingPrincipal * share;
        scaled.fees = entry.fees * share;
        scaled.totalCashout = entry.totalCashout * share;
        return scaled;
    });

    return projected;
}

/**
 * Loans relevant to a unit's finances: its direct loans PLUS a projected view
 * of every building loan that allocates to it. This is the single read-side
 * entry point every per-unit consumer routes through (LoanStore, cashflow
 * aggregation, building financials), so a building loan surfaces correctly on
 * each unit AND its parts recompose the whole at the building level.
 *
 * Behaviour is purely additive: a unit with no building-loan allocation gets
 * exactly the same direct loans as before.
 */
export function projectLoansForProperty(loans: readonly Loan[], propertyUID: string): Loan[] {
    if (!propertyUID) return [];

    const result: Loan[] = [];
    for (const loan of loans) {
        if (loan.buildingGroupUID && loan.allocations && loan.allocations.length > 0) {
            const allocation = loan.allocations.find(a => a.propertyUID === propertyUID);
            if (allocation) {
                result.push(scaleLoanToShare(loan, allocation));
            }
        } else if (!loan.buildingGroupUID && loan.propertyUID === propertyUID) {
            result.push(loan);
        }
    }
    return result;
}
